import { Result } from '@/reservation/reservation.model';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, finalize, switchMap } from 'rxjs';
import { ShowComponent } from 'src/app/common/component/show.component';
import { SectionService } from '../section.service';
import { SectionsApiService } from '../sections.api.service';
import { Product, SectionMarks } from '../sections.model';

@Component({
	selector: 'app-section',
	templateUrl: './section.component.html',
	styleUrls: ['./section.component.scss'],
})
export class SectionComponent extends ShowComponent {
	pageTitle = 'Sekcje';
	data = {
		id: null,
		name: null,
		nameSuffix: '',
		kind: null,
		link: null,
		order: null,
		isActive: false,
	};
	userInput = '';
	products: Product[];
	readonly filteredProducts = signal<Product[]>([]);
	readonly selectedProductIds = signal<number[]>([]);
	readonly search$ = new BehaviorSubject<string>('');
	readonly isLoading = signal(false);
	readonly selectedProductNames = computed(() =>
		this.selectedProductIds().map((id) => ({
			id,
			name: this.productNameMap.get(id) || 'Nieznany produkt',
		})),
	);
	readonly SectionMarks = SectionMarks;

	protected apiType = 'bag';
	protected apiUrlAdd = '/www/section/';
	protected itemsBeforeDataLoad = 0;
	protected service = new SectionService();

	private productNameMap = new Map<number, string>();
	private readonly destroyRef = inject(DestroyRef);
	private readonly sectionsApiService = inject(SectionsApiService);

	selectProduct(product: Product): void {
		if (!this.selectedProductIds().includes(product.id)) {
			this.selectedProductIds.set([...this.selectedProductIds(), product.id]);
		}
		this.userInput = this.search$.getValue();
		this.updateLink();
	}

	removeProduct(productId: number): void {
		if (!this.editable) {
			return;
		}
		this.selectedProductIds.set(this.selectedProductIds().filter((id) => id !== productId));
		this.updateLink();
	}

	generateFinalUrl(): string {
		let finalUrl = this.data.link || '';

		if (this.selectedProductIds().length) {
			const productQuery = `products=${this.selectedProductIds().join(',')}`;

			if (finalUrl.includes('products=')) {
				finalUrl = finalUrl.replace(/([&?])products=[^&]*/, `$1${productQuery}`);
			} else {
				finalUrl += finalUrl.includes('?') ? '&' + productQuery : '?' + productQuery;
			}
		}

		return finalUrl;
	}

	filterProducts(input: string): void {
		this.filteredProducts.set(
			this.products.filter((product) => product.name.toLowerCase().includes(input.toLowerCase())),
		);
	}

	submit(): void {
		this.loader.show();
		this.updateLink();
		this.submitBefore();
		switch (this.pageFunction) {
			case 'add':
				this.bag.get('/www/section/', (result, error) => {
					this.data.order = result.results.length;
					this.doAdd();
				});
				break;
			case 'edit':
				this.doEdit();
				break;
		}
	}

	protected getApiUrl(): string {
		return this.apiUrlAdd + this.data.id + '/';
	}

	protected loadBefore(): void {
		this.isLoading.set(true);
		this.search$
			.pipe(
				debounceTime(300),
				filter((value) => value.trim().length >= 3 || value.trim().length === 0),
				distinctUntilChanged(),
				switchMap((value) => this.sectionsApiService.getProducts(value)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe({
				next: (result: Result<Product>) => {
					this.filteredProducts.set(result.results);
				},
			});

		this.sectionsApiService
			.getProducts('&limit=500')
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((result: Result<Product> | null) => {
				if (result) {
					this.products = result.results;
					this.filteredProducts.set([...this.products]);
					this.buildProductNameMap();
					this.extractProductIdsFromUrl();
				}
			});
		this.loadData();
	}

	protected pageTitleGenerate(): string {
		return this.data.name + ' (ID: ' + this.data.id + ')';
	}

	protected afterSend(result, error): void {
		this.loader.hide();
		if (error) {
			return;
		}
		this.router.navigate([this.parentComponent, this.data.id]);
		this.pageFunction = 'edit';
		this.editable = false;
		this.pageTitle = this.pageTitleGenerate();
		this.apiUrl = this.getApiUrl();
		this.infobox.success('Zapisano');
		this.submitAfter();
	}

	private buildProductNameMap(): void {
		this.productNameMap = new Map(this.products.map((product) => [product.id, product.name]));
	}

	private extractProductIdsFromUrl(): void {
		if (this.data.link) {
			const regex = /products=([0-9,]+)/;
			const match = this.data.link.match(regex);

			if (match && match[1]) {
				const ids = match[1].split(',').map((id) => Number(id.trim()));
				this.selectedProductIds.set(ids);
			}
		}
	}

	private updateLink(): void {
		const finalUrl = this.generateFinalUrl();
		this.data.link = finalUrl;
	}
}
